import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, StatCard, Panel, Segmented, Empty } from "@/components/Shared";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Combobox } from "@/components/Combobox";
import { contactOptions } from "@/lib/contacts";
import { BadgePercent, Save, Search, Users2, Tag, Loader2, Zap, ZapOff, Receipt, Truck, ArrowLeftRight, Layers, Percent, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";

// Cari iskonto profilleri: Elektrikçi + Açık Hesap (KDV'siz) = Faturasız Elektrikçi
export const PROFILES = [
  { value: "musteri", label: "Müşteri", icon: Receipt, cls: "bg-sky-100 text-sky-800 border-sky-300" },
  { value: "elektrikci", label: "Faturalı Elektrikçi", icon: Zap, cls: "bg-emerald-100 text-emerald-800 border-emerald-300", hint: "KDV'li · fatura kesilir" },
  { value: "elektrikci_faturasiz", label: "Faturasız Elektrikçi", icon: ZapOff, cls: "bg-amber-100 text-amber-800 border-amber-300", hint: "Açık Hesap · KDV'siz · cari defter" },
  { value: "tedarikci", label: "Tedarikçi", icon: Truck, cls: "bg-slate-100 text-slate-700 border-slate-300" },
  { value: "her_ikisi", label: "Her İkisi", icon: ArrowLeftRight, cls: "bg-violet-100 text-violet-800 border-violet-300" },
];
export const profileOf = (c) => (c?.type === "elektrikci" && c?.vat_exempt ? "elektrikci_faturasiz" : c?.type || "");
export const profileLabel = (key) => PROFILES.find((p) => p.value === key)?.label || key || "—";
const EMPTY_TYPES = { musteri: 0, elektrikci: 0, elektrikci_faturasiz: 0, tedarikci: 0, her_ikisi: 0 };
const G_KEYS = ["cost_discount", "sale_discount", "electrician_discount", "electrician_nobill_discount"];
const num = (v) => Number(v) || 0;
const TH = ({ children, className = "" }) => <TableHead className={`text-[10px] font-bold uppercase tracking-wider font-head ${className}`}>{children}</TableHead>;

export default function Discounts() {
  const [tab, setTab] = useState("global");

  // SERİ RAPORU
  const [sItems, setSItems] = useState([]);
  const [sLoading, setSLoading] = useState(false);
  const [sQuery, setSQuery] = useState("");
  const loadSeriesReport = async () => { setSLoading(true); try { const { data } = await api.get("/discounts/series-report"); setSItems(data); } catch (e) { toast.error(apiError(e)); } finally { setSLoading(false); } };
  useEffect(() => { if (tab === "seri") loadSeriesReport(); /* eslint-disable-next-line */ }, [tab]);

  // GLOBAL
  const [gItems, setGItems] = useState([]);
  const [gEdit, setGEdit] = useState({});
  const [gQuery, setGQuery] = useState("");
  const [gLoading, setGLoading] = useState(false);
  const [gSaving, setGSaving] = useState(null);

  const loadGlobal = async () => {
    setGLoading(true);
    try {
      const { data } = await api.get("/discounts");
      setGItems(data.items);
      const e = {};
      data.items.forEach((i) => { e[i.group] = Object.fromEntries(G_KEYS.map((k) => [k, i[k]])); });
      setGEdit(e);
    } catch (e) { toast.error(apiError(e)); }
    finally { setGLoading(false); }
  };
  useEffect(() => { loadGlobal(); }, []);

  const gDirty = (group) => { const it = gItems.find((x) => x.group === group); return !!it && G_KEYS.some((k) => num(gEdit[group]?.[k]) !== num(it[k])); };
  const gDirtyGroups = useMemo(() => gItems.filter((i) => gDirty(i.group)).map((i) => i.group), [gItems, gEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveGlobal = async (group, silent = false) => {
    const v = gEdit[group] || {};
    setGSaving(group);
    try {
      const { data } = await api.put("/discounts/global", { group, ...Object.fromEntries(G_KEYS.map((k) => [k, num(v[k])])) });
      setGItems((list) => list.map((i) => (i.group === group ? { ...i, ...Object.fromEntries(G_KEYS.map((k) => [k, num(v[k])])) } : i)));
      if (!silent) toast.success(`${group} iskontosu kaydedildi · ${data.updated} ürünün maliyeti güncellendi`);
      return data.updated || 0;
    } catch (e) { toast.error(apiError(e)); return 0; }
    finally { setGSaving(null); }
  };
  const saveAllGlobal = async () => {
    let n = 0;
    for (const g of gDirtyGroups) n += await saveGlobal(g, true);
    toast.success(`${gDirtyGroups.length} grup kaydedildi · ${n} ürünün maliyeti güncellendi`);
  };
  const deleteGroup = async (group, orphan, count) => {
    const msg = orphan || !count
      ? `"${group}" iskonto tanımı tamamen silinecek (bu grupta eşleşen ürün yok). Onaylıyor musunuz?`
      : `"${group}" iskonto tanımı silinecek. Bu gruptaki ${count} ürünün maliyeti liste fiyatına (iskontosuz) dönecek. Onaylıyor musunuz?`;
    if (!await confirm(msg)) return;
    try {
      const { data } = await api.delete(`/discounts/global/${encodeURIComponent(group)}`);
      toast.success(`"${group}" silindi${data.updated ? ` · ${data.updated} ürün maliyeti güncellendi` : ""}`);
      setGItems((list) => list.filter((i) => i.group !== group));
      setGEdit((e) => { const n = { ...e }; delete n[group]; return n; });
    } catch (e) { toast.error(apiError(e)); }
  };
  const gInput = (group, key, testid) => (
    <Input type="number" className={`h-9 text-center tabular-nums font-mono ${num(gEdit[group]?.[key]) !== num(gItems.find((x) => x.group === group)?.[key]) ? "border-primary bg-primary/5" : ""}`} value={gEdit[group]?.[key] ?? 0}
      onChange={(e) => setGEdit({ ...gEdit, [group]: { ...gEdit[group], [key]: e.target.value } })} data-testid={testid} />
  );
  const gFiltered = useMemo(() => gItems.filter((i) => i.group.toLocaleLowerCase("tr").includes(gQuery.toLocaleLowerCase("tr"))), [gItems, gQuery]);
  const avg = (key) => (gItems.length ? Math.round(gItems.reduce((s, i) => s + num(i[key]), 0) / gItems.length) : 0);

  // CARI
  const [contacts, setContacts] = useState([]);
  const [cid, setCid] = useState("");
  const [cData, setCData] = useState(null);
  const [cEdit, setCEdit] = useState({});
  const [cQuery, setCQuery] = useState("");
  const [cLoading, setCLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);

  const loadContacts = () => api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {});
  useEffect(() => { loadContacts(); }, []);

  const loadContact = async (id) => {
    if (!id) { setCData(null); return; }
    setCLoading(true);
    try {
      const { data } = await api.get(`/discounts/contact/${id}`);
      setCData(data);
      const e = {};
      data.items.forEach((i) => { e[i.group] = i.sale_discount == null ? "" : String(i.sale_discount); });
      setCEdit(e);
    } catch (e) { toast.error(apiError(e)); }
    finally { setCLoading(false); }
  };
  const saveContact = async (group) => {
    const raw = cEdit[group];
    const sale = raw === "" || raw == null ? null : Number(raw);
    try {
      await api.put(`/discounts/contact/${cid}`, { group, sale_discount: sale });
      toast.success(sale == null ? `${group}: cari iskontosu kaldırıldı (profil/genel geçerli)` : `${group}: cari iskontosu %${sale} kaydedildi`);
      loadContact(cid);
    } catch (e) { toast.error(apiError(e)); }
  };
  const changeProfile = async (profile) => {
    setProfileSaving(true);
    try {
      const { data } = await api.put(`/contacts/${cid}/profile`, { profile });
      toast.success(`Cari tipi "${profileLabel(data.profile)}" olarak kaydedildi — teklif/fatura kalemleri buna göre iskonto alır`);
      await Promise.all([loadContact(cid), loadContacts()]);
    } catch (e) { toast.error(apiError(e)); }
    finally { setProfileSaving(false); }
  };
  const cFiltered = useMemo(() => (cData?.items || []).filter((i) => i.group.toLocaleLowerCase("tr").includes(cQuery.toLocaleLowerCase("tr"))), [cData, cQuery]);
  const curProfile = PROFILES.find((p) => p.value === cData?.profile);
  const customCount = (cData?.items || []).filter((i) => i.sale_discount != null).length;

  // CARİ TİPİ
  const [typeDisc, setTypeDisc] = useState(EMPTY_TYPES);
  const [typeSaved, setTypeSaved] = useState(EMPTY_TYPES);
  useEffect(() => { api.get("/settings/type-discounts").then((r) => { const v = { ...EMPTY_TYPES, ...r.data }; setTypeDisc(v); setTypeSaved(v); }).catch(() => {}); }, []);
  const typeDirty = Object.keys(EMPTY_TYPES).some((k) => num(typeDisc[k]) !== num(typeSaved[k]));
  const saveTypeDisc = async () => {
    try {
      const payload = Object.fromEntries(Object.keys(EMPTY_TYPES).map((k) => [k, num(typeDisc[k])]));
      await api.put("/settings/type-discounts", payload);
      setTypeSaved(payload);
      toast.success("Cari tipi varsayılan iskontoları kaydedildi");
    } catch (e) { toast.error(apiError(e)); }
  };
  const profileCounts = useMemo(() => { const m = {}; contacts.forEach((c) => { const p = profileOf(c); if (p) m[p] = (m[p] || 0) + 1; }); return m; }, [contacts]);

  return (
    <div className="space-y-6 stagger-in" data-testid="discounts-page">
      <PageHeader title="İskontolar" badge="Fiyatlandırma" subtitle="Marka/grup bazlı maliyet & satış iskontosu · Faturalı / Faturasız elektrikçi oranları · cariye özel satış iskontosu · cari tipi varsayılanları" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="disc-kpis">
        <StatCard label="Marka / Grup" value={gItems.length} icon={Layers} tone="primary" sub="iskonto tanımlı grup" testid="disc-kpi-groups" onClick={() => setTab("global")} active={tab === "global"} />
        <StatCard label="Ort. Maliyet İsk." value={`%${avg("cost_discount")}`} icon={Percent} tone="blue" sub={`Ort. satış isk. %${avg("sale_discount")}`} testid="disc-kpi-avg" />
        <StatCard label="Elektrikçi Oranlı Grup" value={gItems.filter((i) => num(i.electrician_discount) > 0 || num(i.electrician_nobill_discount) > 0).length} icon={Zap} tone="success" sub="faturalı / faturasız oran girilmiş" testid="disc-kpi-elec" />
        <StatCard label="Cari Profilleri" value={contacts.length} icon={Users2} tone="warning" sub={PROFILES.slice(0, 3).map((p) => `${p.label.split(" ")[0]} ${profileCounts[p.value] || 0}`).join(" · ")} testid="disc-kpi-contacts" onClick={() => setTab("cari")} active={tab === "cari"} />
      </div>

      <Segmented value={tab} onChange={setTab} testid="tab" options={[
        ["global", <span key="g" className="inline-flex items-center gap-1.5"><Tag className="h-4 w-4" />Marka / Grup</span>, gItems.length],
        ["cari", <span key="c" className="inline-flex items-center gap-1.5"><Users2 className="h-4 w-4" />Cariye Özel</span>],
        ["type", <span key="t" className="inline-flex items-center gap-1.5"><BadgePercent className="h-4 w-4" />Cari Tipi</span>],
        ["seri", <span key="s" className="inline-flex items-center gap-1.5"><Layers className="h-4 w-4" />Seri Raporu</span>, sItems.length || undefined],
      ]} />

      {tab === "global" && (
        <Panel title="Marka / Grup İskontoları" subtitle="Maliyet iskontosu liste fiyatına uygulanır ve ürün maliyetini hesaplar; satış iskontoları teklif/fatura satırına varsayılan gelir" icon={Tag} testid="global-panel"
          actions={<>
            <div className="relative hidden sm:block">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Marka / grup ara (ACK, VIKO, NYA...)" value={gQuery} onChange={(e) => setGQuery(e.target.value)} className="pl-8 h-8 w-64" data-testid="global-search" />
            </div>
            {gDirtyGroups.length > 0 && <Button size="sm" className="h-8" onClick={saveAllGlobal} disabled={!!gSaving} data-testid="global-save-all"><Save className="h-3.5 w-3.5 mr-1.5" />Tümünü Kaydet ({gDirtyGroups.length})</Button>}
          </>}>
          <div className="relative sm:hidden mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Marka / grup ara" value={gQuery} onChange={(e) => setGQuery(e.target.value)} className="pl-9" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4 text-[11px]">
            <Hint icon={Percent} title="Maliyet İsk." text="Liste fiyatı × (1 − %) = ürün maliyeti. Kaydedince gruptaki tüm ürünler yeniden hesaplanır." />
            <Hint icon={Tag} title="Genel Satış" text="Müşteri / tedarikçi tipli carilerde teklif & fatura satırına gelen varsayılan oran." />
            <Hint icon={Zap} title="Faturalı Elektrikçi" text="Tipi Elektrikçi + KDV'li carilerde otomatik uygulanır." tone="text-emerald-700" />
            <Hint icon={ZapOff} title="Faturasız Elektrikçi" text="Elektrikçi + Açık Hesap (KDV'siz) carilerde; boşsa faturalı oran kullanılır. Cariye özel her zaman öne geçer." tone="text-amber-700" />
          </div>
          <div className="border rounded-xl overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TH>Marka / Grup</TH>
                <TH className="w-32 text-center">Maliyet İsk. (%)</TH>
                <TH className="w-32 text-center">Genel Satış İsk. (%)</TH>
                <TH className="w-36 text-center"><span className="inline-flex items-center gap-1 text-emerald-700"><Zap className="h-3.5 w-3.5" />Faturalı Elek. (%)</span></TH>
                <TH className="w-36 text-center"><span className="inline-flex items-center gap-1 text-amber-700"><ZapOff className="h-3.5 w-3.5" />Faturasız Elek. (%)</span></TH>
                <TH className="w-28"></TH>
              </TableRow></TableHeader>
              <TableBody>
                {gLoading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></TableCell></TableRow>
                ) : gFiltered.map((i) => {
                  const dirty = gDirty(i.group);
                  return (
                    <TableRow key={i.group} className={dirty ? "bg-primary/[0.03]" : ""} data-testid={`global-row-${i.group}`}>
                      <TableCell className="font-head font-bold">{i.group}{i.count != null && <span className="ml-2 text-[10px] font-normal text-muted-foreground tabular-nums" data-testid={`global-count-${i.group}`}>{i.count} ürün</span>}{i.orphan && <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 text-amber-800 border border-amber-300 text-[9px] font-bold px-1.5 py-0.5 uppercase tracking-wider align-middle" data-testid={`global-orphan-${i.group}`} title="Bu iskonto tanımlı ama şu an eşleşen ürün grubu yok (eski / yeniden adlandırılmış grup adı). Görünür kalır; grup adı ürünlerle eşleşince otomatik uygulanır.">Ürünsüz</span>}{dirty && <span className="ml-2 text-[10px] font-bold text-primary uppercase tracking-wider">değişti</span>}</TableCell>
                      <TableCell>{gInput(i.group, "cost_discount", `global-cost-${i.group}`)}</TableCell>
                      <TableCell>{gInput(i.group, "sale_discount", `global-sale-${i.group}`)}</TableCell>
                      <TableCell>{gInput(i.group, "electrician_discount", `global-elec-${i.group}`)}</TableCell>
                      <TableCell>{gInput(i.group, "electrician_nobill_discount", `global-elec-nb-${i.group}`)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Button size="sm" variant={dirty ? "default" : "outline"} className="h-8" onClick={() => saveGlobal(i.group)} disabled={gSaving === i.group} data-testid={`global-save-${i.group}`}>{gSaving === i.group ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}Kaydet</Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => deleteGroup(i.group, i.orphan, i.count)} title="Bu iskonto grubunu (seriyi) sil" data-testid={`global-delete-${i.group}`}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!gLoading && gFiltered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Grup bulunamadı</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </Panel>
      )}

      {tab === "cari" && (
        <Panel title="Cariye Özel Satış İskontosu" subtitle="Seçilen cari için marka/grup bazında satış iskontosu — cariye özel > profil > genel sırasıyla uygulanır" icon={Users2} testid="cari-panel"
          actions={<div className="w-full sm:w-80"><Combobox options={contactOptions(contacts)} value={contacts.find((c) => c.id === cid)?.title || ""} onChange={(title) => { const c = contacts.find((x) => x.title === title); setCid(c?.id || ""); loadContact(c?.id || ""); }} placeholder="Cari seçin" searchPlaceholder="Ünvan / VKN ara..." data-testid="cari-select" /></div>}>
          {!cid ? (
            <div className="text-center py-16 text-muted-foreground border border-dashed rounded-xl" data-testid="cari-empty-hint">
              <Users2 className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="font-semibold text-foreground/80">Cariye özel iskonto için bir cari seçin</p>
              <p className="text-sm mt-1">Carinin tipini (Faturalı / Faturasız Elektrikçi…) buradan seçin; teklif ve fatura kalemleri otomatik bu iskontoyu alır.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cData && (
                <div className="relative rounded-xl border bg-muted/30 pl-5 pr-4 py-4 overflow-hidden flex flex-col md:flex-row md:items-center gap-4" data-testid="cari-profile-card">
                  <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${curProfile?.value?.startsWith("elektrikci") ? (curProfile.value === "elektrikci" ? "bg-[#16A34A]" : "bg-amber-500") : "bg-secondary"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Cari Tipi / İskonto Profili</div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="font-head font-black text-lg truncate">{cData.contact?.title}</span>
                      {curProfile && <Badge variant="outline" className={curProfile.cls} data-testid="cari-profile-badge">{curProfile.label}</Badge>}
                      {customCount > 0 && <Badge variant="outline" className="border-secondary text-secondary">{customCount} özel oran</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {curProfile?.hint ? `${curProfile.hint} · ` : ""}
                      {Number(cData.type_default) > 0 ? `Tip varsayılanı %${cData.type_default} · ` : ""}
                      "Uygulanan" sütunu teklif/fatura satırına otomatik gelen orandır.
                    </p>
                  </div>
                  <div className="w-full md:w-72">
                    <Select value={cData.profile || ""} onValueChange={changeProfile} disabled={profileSaving}>
                      <SelectTrigger data-testid="cari-profile-select">{profileSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <SelectValue placeholder="Cari tipi seçin" />}</SelectTrigger>
                      <SelectContent>
                        {PROFILES.map((p) => <SelectItem key={p.value} value={p.value} data-testid={`cari-profile-${p.value}`}><span className="inline-flex items-center gap-2"><p.icon className="h-3.5 w-3.5" />{p.label}</span></SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Marka / grup ara" value={cQuery} onChange={(e) => setCQuery(e.target.value)} className="pl-9 h-9" data-testid="cari-group-search" />
              </div>
              <div className="border rounded-xl overflow-x-auto">
                <Table>
                  <TableHeader><TableRow className="bg-muted/50">
                    <TH>Marka / Grup</TH>
                    <TH className="w-44 text-center">Cariye Özel İsk. (%)</TH>
                    <TH className="w-32 text-center">Genel</TH>
                    <TH className="w-32 text-center">Uygulanan</TH>
                    <TH className="w-28"></TH>
                  </TableRow></TableHeader>
                  <TableBody>
                    {cLoading ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></TableCell></TableRow>
                    ) : cFiltered.map((i) => {
                      const dirty = (cEdit[i.group] ?? "") !== (i.sale_discount == null ? "" : String(i.sale_discount));
                      return (
                        <TableRow key={i.group} className={dirty ? "bg-primary/[0.03]" : ""} data-testid={`cari-row-${i.group}`}>
                          <TableCell className="font-head font-bold">{i.group}</TableCell>
                          <TableCell>
                            <Input type="number" className={`h-9 text-center tabular-nums font-mono ${dirty ? "border-primary bg-primary/5" : ""}`} placeholder={`Profil: %${i.applied}`}
                              value={cEdit[i.group] ?? ""} onChange={(e) => setCEdit({ ...cEdit, [i.group]: e.target.value })} data-testid={`cari-sale-${i.group}`} />
                          </TableCell>
                          <TableCell className="text-center"><Badge variant="secondary" className="font-mono">%{i.global_sale_discount}</Badge></TableCell>
                          <TableCell className="text-center"><Badge variant="outline" className={`tabular-nums font-mono ${i.sale_discount != null ? "border-secondary text-secondary" : curProfile?.cls || ""}`} data-testid={`cari-applied-${i.group}`}>%{i.applied}</Badge></TableCell>
                          <TableCell>
                            <Button size="sm" variant={dirty ? "default" : "outline"} className="h-8" onClick={() => saveContact(i.group)} data-testid={`cari-save-${i.group}`}><Save className="h-3.5 w-3.5 mr-1.5" />Kaydet</Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {!cLoading && cFiltered.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Grup bulunamadı</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </Panel>
      )}

      {tab === "type" && (
        <Panel title="Cari Tipi Varsayılan İskontoları" subtitle="Bir tipe oran girildiğinde o tipteki carilere teklif/fatura/cari defter oluştururken tüm ürünlere bu oran uygulanır (%0 = marka/grup iskontosu geçerli)" icon={BadgePercent} testid="type-panel"
          actions={<Button size="sm" className="h-8" onClick={saveTypeDisc} disabled={!typeDirty} data-testid="type-disc-save"><Save className="h-3.5 w-3.5 mr-1.5" />Kaydet{typeDirty ? "" : " ✓"}</Button>}>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="type-cards">
            {PROFILES.map((p) => {
              const dirty = num(typeDisc[p.value]) !== num(typeSaved[p.value]);
              return (
                <div key={p.value} className={`relative rounded-xl border bg-card pl-5 pr-4 py-4 overflow-hidden transition-all hover:shadow-md ${dirty ? "border-primary/50" : ""}`} data-testid={`type-row-${p.value}`}>
                  <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${p.value === "elektrikci" ? "bg-[#16A34A]" : p.value === "elektrikci_faturasiz" ? "bg-amber-500" : p.value === "tedarikci" ? "bg-slate-400" : p.value === "her_ikisi" ? "bg-violet-500" : "bg-sky-500"}`} />
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-head font-bold text-sm inline-flex items-center gap-2"><p.icon className="h-4 w-4 text-muted-foreground" />{p.label}{p.value === "musteri" || p.value === "tedarikci" ? "ler" : ""}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">{p.hint || "Varsayılan satış iskontosu"} · {profileCounts[p.value] || 0} cari</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Input type="number" className={`h-10 w-24 text-center tabular-nums font-mono font-bold text-lg ${dirty ? "border-primary bg-primary/5" : ""}`} value={typeDisc[p.value] ?? 0}
                        onChange={(e) => setTypeDisc({ ...typeDisc, [p.value]: e.target.value })} data-testid={`type-disc-${p.value}`} />
                      <span className="text-muted-foreground font-bold">%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {!PROFILES.length && <Empty />}
        </Panel>
      )}

      {tab === "seri" && (
        <Panel title="Seri Bazlı Rapor" subtitle="Her serinin ürün sayısı, markaları, iskonto durumu ve ortalama (genel satış) kâr marjı" icon={Layers} testid="series-report-panel"
          actions={<div className="relative hidden sm:block"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Seri ara" value={sQuery} onChange={(e) => setSQuery(e.target.value)} className="pl-8 h-8 w-56" data-testid="series-search" /></div>}>
          <div className="border rounded-xl overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TH>Seri</TH>
                <TH className="w-24 text-center">Ürün</TH>
                <TH>Marka(lar)</TH>
                <TH className="w-28 text-center">İskonto</TH>
                <TH className="w-40 text-center">Maliyet / Satış İsk.</TH>
                <TH className="w-28 text-center">Ort. Kâr Marjı</TH>
              </TableRow></TableHeader>
              <TableBody>
                {sLoading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></TableCell></TableRow>
                ) : sItems.filter((i) => i.series.toLocaleLowerCase("tr").includes(sQuery.toLocaleLowerCase("tr"))).map((i) => (
                  <TableRow key={i.series} data-testid={`series-row-${i.series}`}>
                    <TableCell className="font-head font-bold">{i.series}</TableCell>
                    <TableCell className="text-center tabular-nums">{i.product_count}</TableCell>
                    <TableCell className="text-xs text-muted-foreground truncate max-w-[220px]">{i.brands.join(", ") || "—"}</TableCell>
                    <TableCell className="text-center">{i.has_discount ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300" data-testid={`series-disc-${i.series}`}>Tanımlı</Badge> : <Badge variant="outline" className="border-amber-300 text-amber-700">Yok</Badge>}</TableCell>
                    <TableCell className="text-center tabular-nums text-xs">{i.cost_discount != null ? `%${i.cost_discount} / %${i.sale_discount}` : "—"}</TableCell>
                    <TableCell className={cn("text-center tabular-nums font-bold", i.avg_margin == null ? "text-muted-foreground" : i.avg_margin >= 0 ? "text-emerald-600" : "text-destructive")} data-testid={`series-margin-${i.series}`}>{i.avg_margin == null ? "—" : `%${i.avg_margin}`}</TableCell>
                  </TableRow>
                ))}
                {!sLoading && sItems.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Seri bulunamadı</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </Panel>
      )}
    </div>
  );
}

const Hint = ({ icon: Icon, title, text, tone = "text-secondary" }) => (
  <div className="rounded-lg border bg-muted/30 px-3 py-2">
    <div className={`font-bold inline-flex items-center gap-1 ${tone}`}><Icon className="h-3.5 w-3.5" />{title}</div>
    <div className="text-muted-foreground mt-0.5 leading-snug">{text}</div>
  </div>
);
